import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { Alert, AlertStats, AlertSeverity } from '@/types'
import { alertApi } from '@/services/alertApi'
import { auditApi } from '@/services/auditApi'

interface AlertState {
  // Data
  alerts: Alert[]
  stats: AlertStats | null

  // Loading states
  loading: boolean
  error: string | null

  // Actions
  setAlerts: (alerts: Alert[]) => void
  addAlert: (alert: Alert) => void
  updateAlert: (alertId: string, updates: Partial<Alert>) => void
  removeAlert: (alertId: string) => void
  acknowledgeAlert: (alertId: string, acknowledgedBy: string) => void
  resolveAlert: (alertId: string, resolvedBy: string) => void
  setStats: (stats: AlertStats) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Async API actions (persist to backend + audit)
  acknowledgeAlertAsync: (alertId: string, user: string) => Promise<void>
  resolveAlertAsync: (alertId: string, user: string, resolution?: string) => Promise<void>

  // Selectors
  getActiveAlerts: () => Alert[]
  getAlertsByAsset: (assetId: string) => Alert[]
  getAlertsBySeverity: (severity: AlertSeverity) => Alert[]
  getCriticalCount: () => number
}

export const useAlertStore = create<AlertState>()(
  devtools(
    (set, get) => ({
      // Initial state
      alerts: [],
      stats: null,
      loading: false,
      error: null,

      // Actions
      setAlerts: (alerts) => set({ alerts }),

      addAlert: (alert) =>
        set((state) => ({
          alerts: [alert, ...state.alerts]
        })),

      updateAlert: (alertId, updates) =>
        set((state) => ({
          alerts: state.alerts.map((alert) =>
            alert.id === alertId ? { ...alert, ...updates } : alert
          )
        })),

      removeAlert: (alertId) =>
        set((state) => ({
          alerts: state.alerts.filter((alert) => alert.id !== alertId)
        })),

      acknowledgeAlert: (alertId, acknowledgedBy) =>
        set((state) => ({
          alerts: state.alerts.map((alert) =>
            alert.id === alertId
              ? {
                  ...alert,
                  status: 'acknowledged' as const,
                  acknowledged_at: new Date().toISOString(),
                  acknowledged_by: acknowledgedBy
                }
              : alert
          )
        })),

      resolveAlert: (alertId, resolvedBy) =>
        set((state) => ({
          alerts: state.alerts.map((alert) =>
            alert.id === alertId
              ? {
                  ...alert,
                  status: 'resolved' as const,
                  resolved_at: new Date().toISOString(),
                  resolved_by: resolvedBy
                }
              : alert
          )
        })),

      setStats: (stats) => set({ stats }),

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      // Async API actions — call backend then update local state
      acknowledgeAlertAsync: async (alertId, user) => {
        await alertApi.acknowledgeAlert(alertId, user)
        get().acknowledgeAlert(alertId, user)
        auditApi.logAction({
          resource: 'ALERT',
          action: 'APPROVE',
          resource_id: alertId,
          details: { acknowledged_by: user },
        })
      },

      resolveAlertAsync: async (alertId, user, resolution) => {
        await alertApi.resolveAlert(alertId, user, resolution)
        get().resolveAlert(alertId, user)
        auditApi.logAction({
          resource: 'ALERT',
          action: 'UPDATE',
          resource_id: alertId,
          details: { resolved_by: user, resolution },
        })
      },

      // Selectors
      getActiveAlerts: () =>
        get().alerts.filter((a) => a.status === 'active'),

      getAlertsByAsset: (assetId) =>
        get().alerts.filter((a) => a.asset_id === assetId),

      getAlertsBySeverity: (severity) =>
        get().alerts.filter((a) => a.severity === severity),

      getCriticalCount: () =>
        get().alerts.filter(
          (a) => a.severity === 'critical' && a.status === 'active'
        ).length
    }),
    { name: 'alert-store' }
  )
)
